import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, TextInput, Label, Badge, Spinner, Checkbox, Tabs } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import { HiOfficeBuilding, HiTrash, HiPlus, HiCog, HiShieldCheck, HiUserGroup, HiChat, HiInformationCircle, HiEye } from 'react-icons/hi';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { tenantStore } from '../stores/tenantStore';
import { authStore } from '../stores/authStore';
import { toastStore } from '../stores/toastStore';
import { Tenant } from '../types';
import LLMSettingsTab from '../components/LLMSettingsTab';
import MonitoringSettingsTab from '../components/MonitoringSettingsTab';


const TenantsPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ tenantId: string; currentStatus: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    loadTenants();
  }, [includeDeleted]);

  const loadTenants = async () => {
    setIsLoading(true);
    try {
      await tenantStore.loadTenants(includeDeleted);
    } catch (err) {
      toastStore.showError('テナント一覧の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'active': { color: 'success', label: '有効' },
      'suspended': { color: 'warning', label: '停止中' },
      'deleted': { color: 'failure', label: '削除済み' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['active'];
    return <Badge color={config.color as any}>{config.label}</Badge>;
  };

  const handleAddTenant = async () => {
    if (!formData.name.trim()) {
      toastStore.showError('テナント名を入力してください');
      return;
    }

    setIsCreating(true);
    try {
      await tenantStore.createTenant({
        name: formData.name,
      });
      toastStore.showSuccess('テナントを作成しました');
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      toastStore.showError('テナントの作成に失敗しました');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditTenant = async () => {
    if (!selectedTenant) return;
    try {
      await tenantStore.updateTenant(selectedTenant.id, {
        name: formData.name,
      });
      toastStore.showSuccess('テナント情報を更新しました');
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      toastStore.showError('テナントの更新に失敗しました');
    }
  };

  const handleDeleteTenant = async (tenantId: string) => {
    const tenant = tenantStore.tenants.find(t => t.id === tenantId);
    if (tenant) {
      setSelectedTenant(tenant);
      setShowDeleteConfirmModal(true);
    }
  };

  const confirmDeleteTenant = async () => {
    if (!selectedTenant) return;

    setIsDeleting(true);
    try {
      await tenantStore.deleteTenant(selectedTenant.id);
      toastStore.showSuccess('テナントを削除しました');
      setShowDeleteConfirmModal(false);
      setSelectedTenant(null);
    } catch (err) {
      toastStore.showError('テナントの削除に失敗しました');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (tenantId: string, currentStatus: string) => {
    setPendingStatusChange({ tenantId, currentStatus });
    setShowStatusConfirmModal(true);
  };

  const confirmToggleStatus = async () => {
    if (!pendingStatusChange) return;

    const newStatus = pendingStatusChange.currentStatus === 'active' ? 'suspended' : 'active';
    const action = newStatus === 'active' ? '有効化' : '停止';

    setIsUpdatingStatus(true);
    try {
      await tenantStore.updateTenant(pendingStatusChange.tenantId, { status: newStatus as any });
      toastStore.showSuccess(`テナントを${action}しました`);
      setShowStatusConfirmModal(false);
      setPendingStatusChange(null);
    } catch (err) {
      toastStore.showError('ステータスの更新に失敗しました');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleManageTenant = (tenant: Tenant) => {
    // Start impersonation
    authStore.startImpersonation(tenant.id, tenant.name);
    // Navigate to dashboard
    navigate('/dashboard');
    toastStore.showSuccess(`${tenant.name} として管理画面に切り替えました`);
  };

  const resetForm = () => {
    setFormData({
      name: '',
    });
    setSelectedTenant(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">テナント管理</h1>
          <p className="text-gray-600">テナントの管理（ルート管理者専用）</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Checkbox
              id="include-deleted"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            <Label htmlFor="include-deleted" className="ml-2 cursor-pointer">
              削除済みテナントを含む
            </Label>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <HiPlus className="mr-2 h-4 w-4" />
            テナント追加
          </Button>
        </div>
      </div>


      {/* テナントテーブル */}
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>テナント名</Table.HeadCell>
                <Table.HeadCell>ステータス</Table.HeadCell>
                <Table.HeadCell>作成日</Table.HeadCell>
                <Table.HeadCell>更新日</Table.HeadCell>
                <Table.HeadCell>アクション</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {tenantStore.tenants.map((tenant) => (
                <Table.Row key={tenant.id} className="bg-white">
                  <Table.Cell className="font-medium">
                    <div className="flex items-center">
                      <HiOfficeBuilding className="mr-2 h-4 w-4 text-gray-400" />
                      {tenant.name}
                    </div>
                  </Table.Cell>
                  <Table.Cell>{getStatusBadge(tenant.status)}</Table.Cell>
                  <Table.Cell>
                    {tenant.createdAt
                      ? format(new Date(tenant.createdAt), 'yyyy/MM/dd')
                      : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {tenant.updatedAt
                      ? format(new Date(tenant.updatedAt), 'yyyy/MM/dd HH:mm')
                      : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {tenant.id === 'root' || tenant.lineAccountId === '@root' ? (
                      <div className="flex items-center text-gray-500">
                        <HiShieldCheck className="mr-2 h-4 w-4" />
                        <span className="text-sm">システムテナント</span>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <Button
                          size="xs"
                          color="info"
                          onClick={() => handleManageTenant(tenant)}
                          title="このテナントとして管理"
                        >
                          <HiUserGroup className="h-3 w-3 mr-1" />
                          管理
                        </Button>
                        <Button
                          size="xs"
                          color="gray"
                          onClick={() => {
                            setSelectedTenant(tenant);
                            setFormData({
                              name: tenant.name,
                            });
                            setShowEditModal(true);
                          }}
                        >
                          <HiCog className="h-3 w-3" />
                          設定
                        </Button>
                        <Button
                          size="xs"
                          color={tenant.status === 'active' ? 'warning' : 'success'}
                          onClick={() => handleToggleStatus(tenant.id, tenant.status)}
                        >
                          {tenant.status === 'active' ? '停止' : '有効化'}
                        </Button>
                        <Button
                          size="xs"
                          color="failure"
                          onClick={() => handleDeleteTenant(tenant.id)}
                          disabled={tenant.status === 'deleted'}
                        >
                          <HiTrash className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </Table.Cell>
                </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>

      {/* テナント追加モーダル */}
      <Modal show={showAddModal} onClose={() => setShowAddModal(false)}>
        <Modal.Header>新規テナント作成</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" value="テナント名" />
              <TextInput
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="例: サンプルテナント"
                required
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleAddTenant} disabled={isCreating}>
            {isCreating ? (
              <>
                <Spinner size="sm" className="mr-2" />
                作成中...
              </>
            ) : (
              '作成'
            )}
          </Button>
          <Button color="gray" onClick={() => setShowAddModal(false)} disabled={isCreating}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>

      {/* テナント設定モーダル（統合版） */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)} size="4xl">
        <Modal.Header>
          <div className="flex items-center">
            <HiCog className="mr-2 h-5 w-5" />
            {selectedTenant?.name} - 設定
          </div>
        </Modal.Header>
        <Modal.Body>
          <Tabs style="underline">
            <Tabs.Item
              title="基本情報"
              icon={HiInformationCircle}
            >
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="edit-name" value="テナント名" />
                  <TextInput
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleEditTenant}>
                    基本情報を更新
                  </Button>
                </div>
              </div>
            </Tabs.Item>
            <Tabs.Item
              title="LLM設定"
              icon={HiChat}
            >
              {selectedTenant && <LLMSettingsTab tenantId={selectedTenant.id} />}
            </Tabs.Item>
            <Tabs.Item
              title="監視"
              icon={HiEye}
            >
              {selectedTenant && <MonitoringSettingsTab tenantId={selectedTenant.id} />}
            </Tabs.Item>
          </Tabs>
        </Modal.Body>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal show={showDeleteConfirmModal} onClose={() => setShowDeleteConfirmModal(false)} size="md">
        <Modal.Header>テナント削除の確認</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900">
              「{selectedTenant?.name}」を削除しますか？
            </p>
            <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
              <span className="font-medium">警告：</span> この操作は取り消すことができません。削除されたテナントのデータは復元できません。
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={confirmDeleteTenant} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                削除中...
              </>
            ) : (
              '削除する'
            )}
          </Button>
          <Button color="gray" onClick={() => setShowDeleteConfirmModal(false)} disabled={isDeleting}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 停止・有効化確認モーダル */}
      <Modal show={showStatusConfirmModal} onClose={() => setShowStatusConfirmModal(false)} size="md">
        <Modal.Header>ステータス変更の確認</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900">
              {(() => {
                const tenant = tenantStore.tenants.find(t => t.id === pendingStatusChange?.tenantId);
                const action = pendingStatusChange?.currentStatus === 'active' ? '停止' : '有効化';
                return `「${tenant?.name}」を${action}しますか？`;
              })()}
            </p>
            {pendingStatusChange?.currentStatus === 'active' && (
              <div className="p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded">
                停止中のテナントは、管理画面へのログインおよびLINE通知機能が利用できなくなります。
              </div>
            )}
            {pendingStatusChange?.currentStatus === 'suspended' && (
              <div className="p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded">
                テナントを有効化すると、管理画面へのログインおよびすべての機能が利用可能になります。
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color={pendingStatusChange?.currentStatus === 'active' ? 'warning' : 'success'}
            onClick={confirmToggleStatus}
            disabled={isUpdatingStatus}
          >
            {isUpdatingStatus ? (
              <>
                <Spinner size="sm" className="mr-2" />
                処理中...
              </>
            ) : (
              pendingStatusChange?.currentStatus === 'active' ? '停止する' : '有効化する'
            )}
          </Button>
          <Button color="gray" onClick={() => setShowStatusConfirmModal(false)} disabled={isUpdatingStatus}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default TenantsPage;